import { CloseButton, Dialog, HStack, Portal, Tabs } from "@chakra-ui/react";
import { useState } from "react";
import { SETTINGS_TOUR } from "@/config/tours";
import { usePermissions } from "@/hooks/usePermissions";
import { hasSeenTour, useTour } from "@/hooks/useTour";
import GeneralTab from "./settings/GeneralTab";
import PaymentMethodsTab from "./settings/PaymentMethodsTab";
import BankAccountTab from "./settings/BankAccountTab";
import UsersTab from "./settings/UsersTab";
import ApiTab from "./settings/ApiTab";

type Props = {
  open: boolean;
  onClose: () => void;
};

const SettingsModal = ({ open, onClose }: Props): JSX.Element => {
  const { isMerchantAdmin, isDeveloper } = usePermissions();
  // The tour's buttons render outside the dialog content, so outside-click and
  // ESC must not close the modal until the tour is done. Developers who are
  // not admins never see that tour.
  const [tourDone, setTourDone] = useState(() => hasSeenTour("configuracion"));
  useTour("configuracion", SETTINGS_TOUR, open && isMerchantAdmin, () =>
    setTourDone(true)
  );
  const canDismiss = !isMerchantAdmin || tourDone;

  return (
    <Dialog.Root
      open={open}
      closeOnInteractOutside={canDismiss}
      closeOnEscape={canDismiss}
      onOpenChange={(e) => {
        if (!e.open) onClose();
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="md" maxWidth="900px">
            <Dialog.Body py={6}>
              <Tabs.Root
                key={isMerchantAdmin ? "admin" : "developer"}
                defaultValue={isMerchantAdmin ? "general" : "api"}
                variant="line"
                colorPalette="palomma"
              >
                <HStack align="center" justify="space-between" gap={2} mb={4}>
                  <Tabs.List flex="1" minW={0}>
                    {isMerchantAdmin && (
                      <>
                        <Tabs.Trigger
                          value="general"
                          data-tour="settings-tab-general"
                        >
                          General
                        </Tabs.Trigger>
                        <Tabs.Trigger
                          value="metodos"
                          data-tour="settings-tab-metodos"
                        >
                          Métodos de pago
                        </Tabs.Trigger>
                        <Tabs.Trigger
                          value="cuenta"
                          data-tour="settings-tab-cuenta"
                        >
                          Cuenta bancaria
                        </Tabs.Trigger>
                        <Tabs.Trigger
                          value="usuarios"
                          data-tour="settings-tab-usuarios"
                        >
                          Usuarios
                        </Tabs.Trigger>
                      </>
                    )}
                    {isDeveloper && (
                      <Tabs.Trigger value="api">API</Tabs.Trigger>
                    )}
                  </Tabs.List>
                  <CloseButton onClick={onClose} flexShrink={0} />
                </HStack>
                {isMerchantAdmin && (
                  <>
                    <Tabs.Content value="general">
                      <GeneralTab />
                    </Tabs.Content>
                    <Tabs.Content value="metodos">
                      <PaymentMethodsTab />
                    </Tabs.Content>
                    <Tabs.Content value="cuenta">
                      <BankAccountTab />
                    </Tabs.Content>
                    <Tabs.Content value="usuarios">
                      <UsersTab />
                    </Tabs.Content>
                  </>
                )}
                {isDeveloper && (
                  <Tabs.Content value="api">
                    <ApiTab />
                  </Tabs.Content>
                )}
              </Tabs.Root>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default SettingsModal;
